import React, { useState, useEffect } from 'react';

const AdminUserManager = () => {
    const [adminUsers, setAdminUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPhone, setEditingPhone] = useState({});
    const [phoneNumbers, setPhoneNumbers] = useState({});
    const [sendingReset, setSendingReset] = useState({});
    const [deletingUser, setDeletingUser] = useState({});
    
    // Get CSRF token from the DOM, same as Inviter component
    const csrfToken = document.getElementsByName('csrf-token')[0];
    const authenticationToken = csrfToken != null ? csrfToken.getAttribute('content') : '';

    useEffect(() => {
        fetchAdminUsers();
    }, []);

    const fetchAdminUsers = async () => {
        try {
            const response = await fetch('/api/admin/users', {
                headers: {
                    'X-CSRF-Token': authenticationToken,
                    'Accept': 'application/json',
                }
            });
            const data = await response.json();
            setAdminUsers(data.users);
            
            // Initialize phone numbers
            const phones = {};
            data.users.forEach(user => {
                phones[user.id] = user.phone_number || '';
            });
            setPhoneNumbers(phones);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching admin users:', error);
            setLoading(false);
        }
    };

    const handleSendPasswordReset = async (userId, username) => {
        if (!confirm(`Send password reset email to ${username}?`)) {
            return;
        }

        setSendingReset({ ...sendingReset, [userId]: true });
        
        try {
            const response = await fetch('/api/admin/send_password_reset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': authenticationToken,
                },
                body: JSON.stringify({ username: username })
            });
            
            const data = await response.json();
            if (data.success) {
                alert('Password reset email sent successfully');
            } else {
                alert('Failed to send password reset email');
            }
        } catch (error) {
            console.error('Error sending password reset:', error);
            alert('Error sending password reset email');
        } finally {
            setSendingReset({ ...sendingReset, [userId]: false });
        }
    };

    const handleDeleteAdmin = async (userId, username) => {
        // Prevent deletion of protected users
        const protectedUsers = ['danny@fermly.com', 'frances@fermly.com', 'emily@fermly.com'];
        if (protectedUsers.includes(username)) {
            alert('This admin user cannot be deleted.');
            return;
        }

        if (!confirm(`Are you sure you want to delete admin user ${username}? This action cannot be undone.`)) {
            return;
        }

        setDeletingUser({ ...deletingUser, [userId]: true });
        
        try {
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': authenticationToken,
                }
            });
            
            const data = await response.json();
            if (data.success) {
                // Remove user from list
                setAdminUsers(adminUsers.filter(user => user.id !== userId));
                alert('Admin user deleted successfully');
            } else {
                alert(data.message || 'Failed to delete admin user');
            }
        } catch (error) {
            console.error('Error deleting admin:', error);
            alert('Error deleting admin user');
        } finally {
            setDeletingUser({ ...deletingUser, [userId]: false });
        }
    };

    const handleUpdatePhoneNumber = async (userId) => {
        const phoneNumber = phoneNumbers[userId];
        
        try {
            const response = await fetch(`/api/admin/users/${userId}/update_phone`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': authenticationToken,
                },
                body: JSON.stringify({ phone_number: phoneNumber })
            });
            
            const data = await response.json();
            if (data.success) {
                // Update the user in the list
                setAdminUsers(adminUsers.map(user => 
                    user.id === userId ? { ...user, phone_number: phoneNumber } : user
                ));
                setEditingPhone({ ...editingPhone, [userId]: false });
                alert('Phone number updated successfully');
            } else {
                alert('Failed to update phone number');
            }
        } catch (error) {
            console.error('Error updating phone number:', error);
            alert('Error updating phone number');
        }
    };

    const handlePhoneChange = (userId, value) => {
        setPhoneNumbers({ ...phoneNumbers, [userId]: value });
    };

    const togglePhoneEdit = (userId) => {
        setEditingPhone({ ...editingPhone, [userId]: !editingPhone[userId] });
        // Reset phone number if cancelling edit
        if (editingPhone[userId]) {
            const user = adminUsers.find(u => u.id === userId);
            setPhoneNumbers({ ...phoneNumbers, [userId]: user.phone_number || '' });
        }
    };

    if (loading) {
        return (
            <div className="row margin-top-lg">
                <div className="col-sm-12 text-center">
                    <p>Loading admin users...</p>
                </div>
            </div>
        );
    }

    return (
    <div className="admin-user-manager width-100">
    <div className="row margin-top-lg margin-bottom-lg">
    <div className="col-sm-2 col-md-3"></div>
    <div className="col-sm-8 col-md-6 text-left">
    <div className="settings-header">Admin User Management</div>
    </div>
    <div className="col-sm-2 col-md-3"></div>
    </div>

    <div className="row margin-bottom-lg margin-top-md">
    <div className="col-sm-2 col-md-3"></div>
    <div className="col-sm-8 col-md-6">
    <div className="width-100 text-center">
    <div className="admin-users-table">
    <table className="table width-100">
    <thead>
    <tr>
        <th>Email</th>
        <th>Name</th>
        <th>SMS Phone</th>
            <th>Actions</th>
            </tr>
        </thead>
    <tbody>
    {adminUsers.map(user => (
    <tr key={user.id}>
        <td>{user.username}</td>
        <td>{user.first} {user.last}</td>
    <td>
    {editingPhone[user.id] ? (
    <div className="phone-edit-group">
    <input 
        type="text"
        className="form-input phone-input"
        value={phoneNumbers[user.id]}
        onChange={(e) => handlePhoneChange(user.id, e.target.value)}
            placeholder="Ex: +1234567890"
        />
    <button 
        className="btn-small btn-save"
            onClick={() => handleUpdatePhoneNumber(user.id)}
    >
            Save
        </button>
    <button 
        className="btn-small btn-cancel"
            onClick={() => togglePhoneEdit(user.id)}
    >
            Cancel
            </button>
            </div>
    ) : (
    <div className="phone-display-group">
        <span>{user.phone_number || 'Not set'}</span>
    <a 
        className="edit-link"
            onClick={() => togglePhoneEdit(user.id)}
    >
            (edit)
            </a>
            </div>
            )}
        </td>
    <td>
    <button 
        className="btn-action btn-reset"
        onClick={() => handleSendPasswordReset(user.id, user.username)}
            disabled={sendingReset[user.id]}
    >
            {sendingReset[user.id] ? 'Sending...' : 'Send Password Reset'}
        </button>
    {user.username !== 'danny@fermly.com' && user.username !== 'frances@fermly.com' && user.username !== 'emily@fermly.com' && (
    <button 
        className="btn-action btn-delete"
        onClick={() => handleDeleteAdmin(user.id, user.username)}
            disabled={deletingUser[user.id]}
    >
            {deletingUser[user.id] ? 'Deleting...' : 'Delete'}
            </button>
            )}
            </td>
            </tr>
            ))}
            </tbody>
            </table>
            </div>
        </div>
        </div>
            <div className="col-sm-2 col-md-3"></div>
            </div>
        </div>
    );
};

export default AdminUserManager;
